import {
	ContractSuretyType,
	formatToDollar,
	PelContractorType,
	PelRegion,
	pelContractorTypeSchema,
	pelRegionSchema,
} from "@inrev/common";
import { Option } from "@inrev/core";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { EMPTY_PEL_PML_FOR_QUOTE_OVERRIDES, useBondRequestPelPml } from "@portal/api/pel-pml/admin";
import { FormItemLabel } from "@portal/components/form";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { Banner, Card, Icon, Spinner, Text } from "@portal/components/primitives";
import { useMemo, useState } from "react";
import { PelPmlBreakdown } from "../../../shared/PelPmlBreakdown";

export type AdminBondRequestPelPmlCardProps = {
	request: AdminBondRequest;
};

export const AdminBondRequestPelPmlCard = ({ request }: AdminBondRequestPelPmlCardProps) => {
	const [typeOverride, setTypeOverride] = useState<PelContractorType | undefined>();
	const [regionOverride, setRegionOverride] = useState<PelRegion | undefined>();

	const overrides = useMemo(
		() =>
			typeOverride === undefined && regionOverride === undefined
				? EMPTY_PEL_PML_FOR_QUOTE_OVERRIDES
				: {
						...(typeOverride !== undefined ? { contractorType: typeOverride } : {}),
						...(regionOverride !== undefined ? { region: regionOverride } : {}),
					},
		[typeOverride, regionOverride],
	);

	const query = useBondRequestPelPml(request.id, overrides);
	const data = query.data;

	const contractorType = Option.toUndefined(Option.first([typeOverride, data?.contractorType]));
	const region = Option.toUndefined(Option.first([regionOverride, data?.region]));
	const canShowResults = Option.isSome(data?.thisBond) || Option.isSome(data?.accountContext);
	const isWaitingForDefaults = query.isPending || (query.isFetching && Option.isNone(data));

	const isBidRequest = request.contractSuretyType === ContractSuretyType.bid;
	const showBidAmountNote = isBidRequest && request.bidAmount !== request.bondAmount;
	const requestAmountLabel = isBidRequest ? "bid amount" : "bond amount";

	return (
		<Card elevation="raised" className="rounded-lg" data-testid="admin-bond-request-pel-pml">
			<Card.Body className="flex flex-col gap-6 p-6">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<div className="flex flex-col gap-3">
						<FormItemLabel>Type of Contractor</FormItemLabel>
						<DropdownInput
							value={contractorType ?? ""}
							onChange={(value) => {
								const parsed = pelContractorTypeSchema.safeParse(value);
								if (parsed.success) setTypeOverride(parsed.data);
							}}
							options={PelContractorType.options()}
							placeholder="Select a contractor type"
							testId="pel-pml-contractor-type"
						/>
					</div>
					<div className="flex flex-col gap-3">
						<FormItemLabel>Region of Operation</FormItemLabel>
						<DropdownInput
							value={region ?? ""}
							onChange={(value) => {
								const parsed = pelRegionSchema.safeParse(value);
								if (parsed.success) setRegionOverride(parsed.data);
							}}
							options={PelRegion.options()}
							placeholder="Select a region"
							testId="pel-pml-region"
						/>
					</div>
				</div>
				<Text type="caption" color="tertiary">
					Contractor type defaults from this request's scope of work. Region defaults from the
					project location, or the principal's address if the project has none. New York defaults to
					Northeast; New York City/Metropolitan and Multi-Region can only be selected here.
				</Text>
				{showBidAmountNote ? (
					<Text type="caption" color="tertiary" data-testid="pel-pml-bid-amount-note">
						This estimate uses the bid amount ({formatToDollar(request.bidAmount, true)}), not the
						bond amount ({formatToDollar(request.bondAmount, true)}).
					</Text>
				) : null}

				{query.isError ? (
					<Banner tone="danger">
						<Banner.Leading>
							<Icon type="exclamation-circle" size="small" color="danger" />
						</Banner.Leading>
						<Banner.Content>
							<Text type="body" color="danger">
								The calculation could not be completed.
							</Text>
						</Banner.Content>
					</Banner>
				) : canShowResults && Option.isSome(data) ? (
					<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
						<div className="flex flex-col gap-3">
							<div className="flex flex-col gap-3.5">
								<Text type="title" color="primary">
									This bond
								</Text>
								<Text type="caption" color="tertiary">
									{`Total exposure is the ${requestAmountLabel} (${formatToDollar(data.bondAmount, true)})`}
								</Text>
							</div>
							{Option.isSome(data.thisBond) ? (
								<PelPmlBreakdown result={data.thisBond} testIdPrefix="pel-pml-this-bond" />
							) : (
								<Text type="body" color="tertiary">
									Enter a {requestAmountLabel} to estimate this request on its own.
								</Text>
							)}
						</div>
						<div className="flex flex-col gap-3">
							<div className="flex flex-col gap-3.5">
								<Text type="title" color="primary">
									Account including this request
								</Text>
								<Text type="caption" color="tertiary">
									{formatToDollar(data.accountBondExposure, true)} existing bonds +{" "}
									{formatToDollar(data.bondAmount, true)} this request
								</Text>
							</div>
							{Option.isSome(data.accountContext) ? (
								<PelPmlBreakdown result={data.accountContext} testIdPrefix="pel-pml-account" />
							) : (
								<Text type="body" color="tertiary">
									No written bonds or request amount to include.
								</Text>
							)}
						</div>
					</div>
				) : isWaitingForDefaults ? (
					<div className="flex justify-center py-8" data-testid="pel-pml-loading">
						<Spinner />
					</div>
				) : (
					<Text
						type="body"
						color="tertiary"
						className="text-center"
						data-testid="pel-pml-empty-state"
					>
						Select a contractor type and region to see the estimate.
					</Text>
				)}
			</Card.Body>
		</Card>
	);
};
